import React, { useState, useRef, useEffect } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../@/components/ui/popover";
import { Check } from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "../@/components/ui/command";
import countryCodes from "../assets/CountryCodes.json";
import { isMobileScreen } from "../lib/utils";
import { IoMdClose } from "react-icons/io";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../@/components/ui/dialog";

interface PhoneInputProps {
  selectedCode: string;
  selectedNumber: string;
  error: boolean;
  setSelectedCode: (code: string) => void;
  setSelectedNumber: (number: string) => void;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  selectedCode,
  error,
  selectedNumber,
  setSelectedCode,
  setSelectedNumber,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelectChange = (code: string) => {
    setSelectedCode(code);
    setOpen(false);
  };

  const handlePhoneInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const currentValue = input.value;

    const isValidInput = /^\d*$/.test(currentValue);
    if (isValidInput) {
      setSelectedNumber(currentValue);
    }
    if (!isValidInput) {
      input.value = currentValue.replace(/\D/g, "");
    }
  };

  const filteredCodes = countryCodes.filter(
    (country) =>
      country.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.includes(searchTerm)
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchTerm]);

  useEffect(() => {
    if (isMobileScreen && open) {
      // Function to handle keyboard showing
      const handleResize = () => {
        // Calculate keyboard height by comparing window inner height with screen height
        const visualViewportHeight =
          window.visualViewport?.height || window.innerHeight;
        const screenHeight = window.screen.height;
        const estimatedKeyboardHeight = screenHeight - visualViewportHeight;

        if (estimatedKeyboardHeight > 100) {
          // Threshold to detect keyboard
          setKeyboardHeight(estimatedKeyboardHeight);
        } else {
          setKeyboardHeight(0);
        }
      };

      // Add event listeners
      window.visualViewport?.addEventListener("resize", handleResize);
      window.addEventListener("resize", handleResize);

      // Initial check
      handleResize();

      return () => {
        window.visualViewport?.removeEventListener("resize", handleResize);
        window.addEventListener("resize", handleResize);
      };
    }
  }, [isMobileScreen, open]);

  return (
    <div>
      <div
        className={`flex border ${
          error ? "border-red-500" : ""
        } p-0 border-gray-300 overflow-hidden mt-1 focus-within:border-customGreenColor focus-within:outline-customGreenColor rounded-lg`}
      >
        {isMobileScreen ? (
          // Mobile view with Dialog
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                aria-expanded={open}
                className="flex justify-center items-center hover:bg-slate-100 whitespace-nowrap w-20 m-0 p-0 border-r min-h-full border-gray-300 rounded-none"
              >
                {selectedCode}
              </button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-full sm:max-w-md p-0 h-[90vh] flex flex-col z-[10000]">
              <div className="sticky top-0 z-10 bg-white p-4 border-b flex items-center justify-between">
                <input
                  type="text"
                  ref={inputRef}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md mr-2"
                  placeholder="Search phone code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                  onFocus={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <button
                  className="p-2 rounded-full hover:bg-gray-100"
                  onClick={() => setOpen(false)}
                >
                  <IoMdClose className="h-6 w-6" />
                </button>
              </div>

              <div
                className="overflow-y-auto flex-1 -webkit-overflow-scrolling: touch"
                style={{
                  paddingBottom: keyboardHeight
                    ? `${keyboardHeight + 20}px`
                    : "0px",
                }}
              >
                {filteredCodes.length > 0 ? (
                  <div className="px-4">
                    {filteredCodes.map((country) => (
                      <div
                        key={country.country}
                        className="p-3 cursor-pointer hover:bg-gray-100 text-gray-700 border-b last:border-b-0 flex items-center justify-between"
                        onClick={() => handleSelectChange(country.code)}
                      >
                        <span>
                          {country.country} ({country.code})
                        </span>
                        {selectedCode === country.code && (
                          <Check className="h-4 w-4" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-gray-500">No phone code found.</div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        ) : (
          // Desktop view with Popover
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                aria-expanded={open}
                className="flex justify-center items-center hover:bg-slate-100 whitespace-nowrap w-20 m-0 p-0 border-r min-h-full border-gray-300 rounded-none"
              >
                {selectedCode}
              </button>
            </PopoverTrigger>
            <PopoverContent className="absolute z-10 w-full left-0 min-w-60 xxl:min-w-72 max-h-20 p-0">
              <Command>
                <CommandInput
                  placeholder="Search phone code..."
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No phone code found.</CommandEmpty>
                  <CommandGroup>
                    {filteredCodes.map((country) => (
                      <CommandItem
                        key={country.country}
                        onSelect={() => handleSelectChange(country.code)}
                        className="flex items-center justify-between p-2 hover:bg-gray-200"
                      >
                        {country.country} ({country.code})
                        {selectedCode === country.code && (
                          <Check className="h-4 w-4" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        <input
          autoComplete="new-password"
          type="tel"
          maxLength={10}
          value={selectedNumber}
          className="block w-full px-2 xxl:py-3 py-2 peer focus:outline-none focus:ring-0"
          onChange={handlePhoneInput}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
